import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, Package } from 'lucide-react';
import { Input } from './ui/input';
import fetchWithAuth from '../services/fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token');

/**
 * RM Library Material Selector for BOM Editor.
 * Replaces the old material_conversion_profiles dropdown.
 *
 * Props:
 *  - value: current profile_code or material_profile_id
 *  - onChange(profileCode, adaptedProfile): called when user selects a material
 *  - disabled: whether the selector is disabled
 */
export const RMLibrarySelector = ({ value, onChange, disabled = false }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [displayLabel, setDisplayLabel] = useState('');
  const [adaptedProfile, setAdaptedProfile] = useState(null);
  const timerRef = useRef(null);
  const containerRef = useRef(null);

  // Resolve display label for current value
  useEffect(() => {
    if (!value) {
      setDisplayLabel('');
      setAdaptedProfile(null);
      return;
    }
    const tk = getToken();
    if (!tk) return;
    fetchWithAuth(`${API}/api/v1/rm-library/as-material-profile/${encodeURIComponent(value)}`, {
      headers: { Authorization: `Bearer ${tk}` },
    })
      .then(async (res) => {
        if (res.ok) {
          const p = await res.json();
          setDisplayLabel(p.profile_name || p.profile_code || value);
          setAdaptedProfile(p);
          // Notify parent with resolved profile so isLinearProfile detection works on load
          onChange(value, p);
        } else {
          setDisplayLabel(value);
          setAdaptedProfile(null);
        }
      })
      .catch(() => {
        setDisplayLabel(value);
        setAdaptedProfile(null);
      });
  }, [value]);

  // Debounced search
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (query.length < 2) { setResults([]); return; }
    setSearching(true);
    timerRef.current = setTimeout(async () => {
      const tk = getToken();
      try {
        const res = await fetchWithAuth(
          `${API}/api/v1/rm-library/search-for-bom?q=${encodeURIComponent(query)}&limit=15`,
          { headers: { Authorization: `Bearer ${tk}` } }
        );
        if (res.ok) setResults(await res.json());
      } catch (e) { console.error(e); }
      setSearching(false);
    }, 250);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = useCallback(async (item) => {
    const code = item.profile_code;
    setQuery('');
    setOpen(false);
    setResults([]);

    // Fetch adapted profile
    const tk = getToken();
    try {
      const res = await fetchWithAuth(
        `${API}/api/v1/rm-library/as-material-profile/${encodeURIComponent(code)}`,
        { headers: { Authorization: `Bearer ${tk}` } }
      );
      if (res.ok) {
        const adapted = await res.json();
        setDisplayLabel(adapted.profile_name || code);
        setAdaptedProfile(adapted);
        onChange(code, adapted);
      } else {
        onChange(code, null);
      }
    } catch {
      onChange(code, null);
    }
  }, [onChange]);

  const handleClear = () => {
    setDisplayLabel('');
    setAdaptedProfile(null);
    onChange('', null);
  };

  return (
    <div ref={containerRef} className="relative" data-testid="rm-library-selector">
      {/* Selected value display or search input */}
      {value && !open ? (
        <div
          className="flex items-center justify-between h-8 px-2 text-xs rounded border border-slate-600 bg-slate-800 cursor-pointer hover:border-slate-500 transition-colors"
          onClick={() => { if (!disabled) { setOpen(true); setQuery(''); } }}
          data-testid="rm-selector-display"
        >
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <Package className="w-3 h-3 text-amber-400 shrink-0" />
            <span className="text-slate-200 truncate">{displayLabel || value}</span>
          </div>
          {!disabled && (
            <button
              onClick={(e) => { e.stopPropagation(); handleClear(); }}
              className="text-slate-500 hover:text-slate-300 ml-1 shrink-0"
              data-testid="rm-selector-clear"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500" />
          <Input
            autoFocus={open}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Search RM Library — type designation, grade..."
            className="h-8 text-xs bg-slate-800 border-slate-600 text-white pl-7 pr-2"
            disabled={disabled}
            data-testid="rm-selector-search"
          />
          {searching && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-amber-400 animate-pulse">
              searching...
            </span>
          )}
        </div>
      )}

      {/* Dropdown results */}
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 max-h-64 overflow-y-auto rounded-lg border border-slate-600 bg-slate-900 shadow-xl" data-testid="rm-selector-results">
          {results.map((item) => {
            const identity = item.identity || {};
            const dims = item.dimensions || {};
            const sec = item.sectional_properties || {};
            return (
              <button
                key={item.profile_code}
                onClick={() => handleSelect(item)}
                className="w-full text-left px-3 py-2 text-xs hover:bg-slate-800 border-b border-slate-800 last:border-b-0 transition-colors"
                data-testid={`rm-result-${item.profile_code}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {item.hpn_display && <span className="font-mono text-[9px] text-blue-400/70">{item.hpn_display}</span>}
                    <span className="font-mono font-semibold text-slate-200">
                      {identity.designation || item.profile_code}
                    </span>
                  </div>
                  <span className="text-[9px] text-amber-400/70 px-1.5 py-0.5 rounded bg-amber-900/20 border border-amber-700/30">
                    {identity.section_type || ''}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-[10px] text-slate-500">
                  <span>{identity.material_grade || ''}</span>
                  {sec.mass_per_metre_kg && <span>{sec.mass_per_metre_kg} kg/m</span>}
                  {dims.D_mm && <span>D:{dims.D_mm}</span>}
                  {dims.B_mm && <span>B:{dims.B_mm}</span>}
                  {dims.od_mm && <span>OD:{dims.od_mm}</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
      {open && query.length >= 2 && results.length === 0 && !searching && (
        <div className="absolute z-50 w-full mt-1 p-3 text-xs text-slate-500 text-center rounded-lg border border-slate-600 bg-slate-900">
          No materials found for "{query}"
        </div>
      )}
    </div>
  );
};

export default RMLibrarySelector;
